import { Delete02Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { api } from "@slapinc/slappos-backend/api";
import { createFileRoute } from "@tanstack/react-router";
import { useConvex, useMutation, useQuery } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { Download, FileText } from "lucide-react";
import {
	lazy,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Can } from "@/components/can";
import { PageContent } from "@/components/layout/page-content";
import { CreateReportModal } from "@/components/modals/create-report-modal";
import { ActionHeaderButton } from "@/components/ui/action-header-button";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogMedia,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { EmptyStateWithSkeleton } from "@/components/ui/empty-state-with-skeleton";
import { Modal, ModalContent } from "@/components/ui/modal";
import { Spinner } from "@/components/ui/spinner";
import { SearchInput } from "@/components/ui/updated-search-input";
import { ReportsTable } from "@/features/reports/_components/reports-table";
import { useDebounce } from "@/hooks/use-debounce";
import { useMe } from "@/hooks/use-me";
import { usePagination } from "@/hooks/use-pagination";
import { getReportTypeLabels } from "@/lib/constants/report-types";
import { handleConvexError } from "@/lib/errors";
import { toast } from "@/lib/toast";
import type { Report } from "@/lib/types/report";

const ReportPdfViewer = lazy(
	() => import("@/features/reports/components/report-pdf-viewer"),
);

export const Route = createFileRoute("/(dashboard)/reports")({
	component: ReportsPage,
});

const PAGE_SIZE = 10;

function ReportsPage() {
	const me = useMe();
	const orgId = me?.activeOrganizationId;

	const reportsData = useQuery(
		api.features.reports.web.list,
		orgId ? {} : "skip",
	);
	const branchesData = useQuery(api.features.branches.web.list, {});

	const reports = useMemo(() => reportsData ?? [], [reportsData]);
	const branches = branchesData ?? [];
	const isLoading = reportsData === undefined;

	const convex = useConvex();
	const generateReport = useMutation(api.features.reports.web.generate);
	const removeReport = useMutation(api.features.reports.web.remove);

	const [searchQuery, setSearchQuery] = useState("");
	const debouncedSearch = useDebounce(searchQuery);

	const { pagination, onPaginationChange, resetPage } =
		usePagination(PAGE_SIZE);

	const [createOpen, setCreateOpen] = useState(false);
	const [isCreating, setIsCreating] = useState(false);

	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [reportToDelete, setReportToDelete] = useState<Report | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	const [viewerOpen, setViewerOpen] = useState(false);
	const [viewerReport, setViewerReport] = useState<Report | null>(null);
	const [viewerUrl, setViewerUrl] = useState<string | null>(null);

	const filteredReports = useMemo(() => {
		if (!debouncedSearch) return reports;
		const query = debouncedSearch.toLowerCase();
		return reports.filter(
			(r) =>
				(r.name ?? "").toLowerCase().includes(query) ||
				(getReportTypeLabels()[r.type] ?? r.type)
					.toLowerCase()
					.includes(query) ||
				(r.branchName ?? "").toLowerCase().includes(query),
		);
	}, [reports, debouncedSearch]);

	const paginatedReports = useMemo(() => {
		const start = pagination.pageIndex * pagination.pageSize;
		return filteredReports.slice(start, start + pagination.pageSize);
	}, [filteredReports, pagination.pageIndex, pagination.pageSize]);

	const isFirstRender = useRef(true);
	// biome-ignore lint/correctness/useExhaustiveDependencies: debouncedSearch is the intentional trigger
	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}
		resetPage();
	}, [debouncedSearch, resetPage]);

	const handleCreate = useCallback(
		async (data: {
			name?: string;
			type: string;
			branchId?: Id<"branches">;
			startDate: string;
			endDate: string;
		}) => {
			if (!orgId) return;
			setIsCreating(true);
			try {
				await generateReport({
					orgId,
					name: data.name,
					type: data.type as Parameters<typeof generateReport>[0]["type"],
					branchId: data.branchId,
					startDate: data.startDate,
					endDate: data.endDate,
				});
				toast.success(<>Report generation started</>);
			} catch (error) {
				handleConvexError(error, "Failed to create report");
				throw error;
			} finally {
				setIsCreating(false);
			}
		},
		[orgId, generateReport],
	);

	const getDownloadUrl = useCallback(
		async (report: Report, format: "pdf" | "xlsx") => {
			if (!orgId || report.status !== "completed") return null;
			const { url } = await convex.query(api.features.reports.web.downloadUrl, {
				orgId,
				reportId: report._id,
				format,
			});
			return url;
		},
		[orgId, convex],
	);

	const handleView = useCallback(
		async (report: Report) => {
			try {
				const url = await getDownloadUrl(report, "pdf");
				if (url) {
					setViewerReport(report);
					setViewerUrl(url);
					setViewerOpen(true);
				}
			} catch (error) {
				handleConvexError(error, "Failed to open report");
			}
		},
		[getDownloadUrl],
	);

	const handleDownload = useCallback(
		async (report: Report, format: "pdf" | "xlsx") => {
			try {
				const url = await getDownloadUrl(report, format);
				if (!url) return;
				const link = document.createElement("a");
				link.href = url;
				link.download = `${report.name}.${format}`;
				document.body.appendChild(link);
				link.click();
				link.remove();
			} catch (error) {
				handleConvexError(error, "Failed to download report");
			}
		},
		[getDownloadUrl],
	);

	const handleDelete = useCallback((report: Report) => {
		setReportToDelete(report);
		setDeleteDialogOpen(true);
	}, []);

	const confirmDelete = useCallback(async () => {
		if (!reportToDelete || !orgId) return;
		setIsDeleting(true);
		try {
			await removeReport({ orgId, reportId: reportToDelete._id });
			toast.deleted("Report");
			setDeleteDialogOpen(false);
			setReportToDelete(null);
		} catch (error) {
			handleConvexError(error, "Failed to delete report");
		} finally {
			setIsDeleting(false);
		}
	}, [reportToDelete, orgId, removeReport]);

	return (
		<PageContent title="Reports">
			<div className="flex flex-1 flex-col gap-4 sm:gap-6 min-w-0">
				<p className="text-sm text-muted-foreground">
					Reports are automatically deleted after 90 days. Please download
					important reports for long-term storage.
				</p>
				<div className="flex items-center justify-end">
					<Can scope="reports.create">
						<ActionHeaderButton
							label="Create Report"
							onClick={() => setCreateOpen(true)}
						/>
					</Can>
				</div>

				{!isLoading && (reports.length > 0 || searchQuery) && (
					<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
						<div className="flex-1 sm:max-w-sm">
							<SearchInput
								placeholder="Search reports..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
							/>
						</div>
					</div>
				)}

				{!isLoading && filteredReports.length === 0 ? (
					<div className="flex flex-1 flex-col justify-end pb-12">
						<EmptyStateWithSkeleton
							title="No reports found"
							description="Create a report to generate insights from your sales and inventory data"
							actionLabel="Create Report"
							onAction={() => setCreateOpen(true)}
							icon={<FileText className="h-5 w-5 text-neutral-700" />}
						/>
					</div>
				) : (
					<ReportsTable
						data={paginatedReports}
						loading={isLoading}
						pagination={pagination}
						onPaginationChange={onPaginationChange}
						rowCount={filteredReports.length}
						onView={handleView}
						onDownload={handleDownload}
						onDelete={handleDelete}
					/>
				)}
			</div>

			<CreateReportModal
				open={createOpen}
				onOpenChange={setCreateOpen}
				branches={branches}
				onSubmit={handleCreate}
				isSubmitting={isCreating}
			/>

			<AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogMedia className="bg-red-50 text-red-600">
							<HugeiconsIcon icon={Delete02Icon} strokeWidth={2} />
						</AlertDialogMedia>
						<AlertDialogTitle>Delete report?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to delete{" "}
							<span className="font-medium text-neutral-900">
								"{reportToDelete?.name}"?
							</span>{" "}
							This action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={confirmDelete}
							disabled={isDeleting}
							className="rounded-xl px-4"
						>
							{isDeleting ? "Deleting..." : "Delete Report"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<Modal
				open={viewerOpen}
				onOpenChange={(open) => {
					setViewerOpen(open);
					if (!open) {
						setViewerReport(null);
						setViewerUrl(null);
					}
				}}
			>
				<ModalContent className="sm:max-w-4xl h-[90vh] p-0 gap-0 overflow-hidden flex flex-col">
					<div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3">
						<div className="min-w-0">
							<h3 className="text-sm font-semibold text-neutral-900 truncate">
								{viewerReport?.name}
							</h3>
							<p className="text-xs text-neutral-500 mt-0.5">
								{viewerReport?.startDate} to {viewerReport?.endDate}
							</p>
						</div>
						<div className="flex items-center gap-2 shrink-0">
							<Button
								variant="outline"
								size="sm"
								onClick={() =>
									viewerReport && handleDownload(viewerReport, "pdf")
								}
							>
								<Download className="size-3.5" />
								PDF
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={() =>
									viewerReport && handleDownload(viewerReport, "xlsx")
								}
							>
								<Download className="size-3.5" />
								Excel
							</Button>
						</div>
					</div>
					<div className="flex-1 overflow-y-auto bg-neutral-100 p-6">
						{viewerUrl && (
							<Suspense
								fallback={
									<div className="flex items-center justify-center py-20">
										<Spinner className="size-6" />
									</div>
								}
							>
								<ReportPdfViewer key={viewerUrl} file={viewerUrl} />
							</Suspense>
						)}
					</div>
				</ModalContent>
			</Modal>
		</PageContent>
	);
}
